import { useEffect, useRef, useState } from "react";

import { t } from "i18next";

import { notificationAtom } from "@/src/state/notificationAtom";

import {
  MAX_FEEDBACK_IMAGES_COUNT,
  MAX_FEEDBACK_INPUT_IMAGE_SIZE_MB
} from "@/src/config/baanaConfig";

import { compressFeedbackImage } from "./feedbackImageCompression";
import { validateFileSize } from "./feedbackUtils";

import { useSetAtom } from "jotai";

export const useFeedbackAttachments = () => {
  const setNotification = useSetAtom(notificationAtom);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    urlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => {
    return () => {
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleFileSelect = async (files: FileList | File[]) => {
    const validFiles: File[] = [];
    const oversizedFiles: string[] = [];

    Array.from(files).forEach((file) => {
      if (validateFileSize(file)) {
        validFiles.push(file);
      } else {
        oversizedFiles.push(file.name);
      }
    });

    if (oversizedFiles.length > 0) {
      setNotification({
        text: t("feedbackForm.error.fileTooLarge", {
          max: MAX_FEEDBACK_INPUT_IMAGE_SIZE_MB,
          files: oversizedFiles.join(", ")
        }),
        variant: "warning"
      });
    }

    if (validFiles.length === 0) return;

    const compressedFiles = await Promise.all(
      validFiles.map((file) => compressFeedbackImage(file))
    );

    setAttachments((prev) => {
      const remaining = MAX_FEEDBACK_IMAGES_COUNT - prev.length;
      if (remaining <= 0) {
        setNotification({
          text: t("feedbackForm.error.maxImagesReached", {
            max: MAX_FEEDBACK_IMAGES_COUNT
          }),
          variant: "warning"
        });
        return prev;
      }
      const toAdd = compressedFiles.slice(0, remaining);
      if (toAdd.length < compressedFiles.length) {
        setNotification({
          text: t("feedbackForm.error.maxImagesReached", {
            max: MAX_FEEDBACK_IMAGES_COUNT
          }),
          variant: "warning"
        });
      }
      const newUrls = toAdd.map((f) => URL.createObjectURL(f));
      setPreviewUrls((prevUrls) => [...prevUrls, ...newUrls]);
      return [...prev, ...toAdd];
    });
  };

  const handleRemoveAttachment = (index: number) => {
    URL.revokeObjectURL(previewUrls[index]);
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const resetAttachments = () => {
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
    setPreviewUrls([]);
    setAttachments([]);
  };

  return {
    attachments,
    previewUrls,
    lightboxIndex,
    setLightboxIndex,
    handleFileSelect,
    handleRemoveAttachment,
    resetAttachments
  };
};
